<template>
  <div class="p-4 h-full flex flex-col gap-3 rounded-6xl glass-frosted overflow-hidden">
    <div class="flex-1 overflow-y-auto">
      <div class="flex flex-wrap gap-2">
        <div
          class="group px-4 flex flex-col items-center justify-center w-42.5 h-26.5 bg-key rounded-4xl overflow-hidden cursor-pointer"
          @click="handleUploadClick"
        >
          <Icon
            icon="Function-Wireframe-Add11"
            class="size-10 text-text-glass-default transition-colors group-hover:text-text-glass-hover group-active:text-brand-blue1"
          />
          <p
            class="text-s-medium text-black-white text-center transition-colors group-active:text-brand-blue1"
          >
            {{ $t('pageAndPlayer.pages.picture.add') }}
          </p>
        </div>
        <div
          v-for="picture in pictures"
          :key="picture.id"
          class="relative w-42.5 h-26.5 bg-key rounded-xl overflow-hidden cursor-pointer"
          @click="handleSelectPicture(picture)"
        >
          <img
            :src="picture.url"
            :alt="picture.name"
            class="w-full h-full object-cover"
            draggable="false"
          />

          <!-- 边框 -->
          <div
            class="absolute inset-0 rounded-xl pointer-events-none border-3 transition-colors"
            :class="
              picture.id === selectedPicture?.id ? 'border-brand-blue1' : 'border-transparent'
            "
          />

          <div class="absolute inset-x-1.5 bottom-1.5 p-1.5 glass-frosted rounded-m">
            <p class="truncate text-s-medium text-black">
              {{ picture.name }}{{ getPictureExtension(picture.mimeType) }}
            </p>
          </div>

          <div class="absolute top-1.5 px-1.5 w-full flex items-center justify-between">
            <div>
              <Icon
                v-show="activatePage?.id === picture.id"
                icon="Switch-Fill-Check"
                class="text-brand-blue1 size-6 transition-opacity"
                :class="activatePage?.id === picture.id ? 'opacity-100' : 'opacity-0'"
              />
            </div>
            <div v-if="!picture.builtIn" class="flex items-center gap-1.5">
              <Button color="glass" size="icon-sm" @click.stop="handleRenamePicture(picture.id)">
                <Icon icon="Writing-Wireframe-Edit" />
              </Button>

              <Button color="glass" size="icon-sm" @click.stop="handleDeletePicture(picture.id)">
                <Icon icon="Function-Wireframe-Delete" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <input
      ref="fileInputRef"
      type="file"
      :accept="FILE_ACCEPTS.join(', ')"
      class="hidden"
      @change="handleFileChange"
    />

    <Dialog
      v-model:open="isDeleteDialogOpen"
      :title="$t('pageAndPlayer.pictureDeleteDialog.title')"
      content-class="w-90"
    >
      <div class="py-2 text-m-medium text-text-glass-default">
        {{
          deletingPictureUseCount > 0
            ? $t('pageAndPlayer.pictureDeleteDialog.occupancyTip')
            : $t('pageAndPlayer.pictureDeleteDialog.confirmTip')
        }}
      </div>
      <template #footer>
        <div class="flex gap-2 justify-end">
          <Button color="text-b-w" @click="isDeleteDialogOpen = false">
            {{ $t('common.cancel') }}
          </Button>
          <Button color="black" @click="handleConfirmDelete">
            {{ $t('common.confirmDelete') }}
          </Button>
        </div>
      </template>
    </Dialog>

    <Dialog
      v-model:open="isRenameDialogOpen"
      :title="$t('pageAndPlayer.pictureEditDialog.title')"
      content-class="w-90"
    >
      <div class="py-2">
        <Input
          v-model="pictureName"
          class="text-black-white text-text-glass-default"
          :placeholder="$t('pageAndPlayer.pictureEditDialog.placeholder')"
          @keydown.enter="handleConfirmRename"
        />

        <div v-if="isPictureNameOverLimit" class="mt-1 text-xs text-red-500">
          {{ $t('pageAndPlayer.pictureEditDialog.error', { limit: MAX_NAME_LENGTH }) }}
        </div>
      </div>
      <template #footer>
        <div class="flex gap-2 justify-end">
          <Button color="text-b-w" @click="isRenameDialogOpen = false">
            {{ $t('common.cancel') }}
          </Button>

          <Button
            color="text-b-w"
            :disabled="isPictureNameOverLimit || !pictureName.trim()"
            @click="handleConfirmRename"
          >
            {{ $t('common.confirm') }}
          </Button>
        </div>
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue'
  import { Button, Dialog, Icon, Input, toast } from '@rapoo/element'
  import { useLocaleStore } from '@/stores'
  import { savePicture, removePicture } from '../../utils/screenPictureDb'
  import {
    getPictureExtension,
    FILE_ACCEPTS,
    FILE_SUFFIX_ACCEPTS,
    MAX_NAME_LENGTH,
    createScreenPictureItem,
  } from '../../utils/pages'

  import type { PageItem, ScreenPictureItem, UserScreenPictureItem } from '../../utils/types'

  const { t } = useLocaleStore()

  const props = defineProps<{
    pages: PageItem[]
    pictures: ScreenPictureItem[]
    activePage: number
    selectedPicture: ScreenPictureItem | null
  }>()

  const emit = defineEmits<{
    (e: 'select', item: ScreenPictureItem): void
    (e: 'update', item?: ScreenPictureItem[]): void
  }>()

  // 当前激活的屏显，用于判断屏显当前使用的素材
  const activatePage = computed(() => props.pages.find((page) => page.slot === props.activePage))

  // 上传素材表单
  const fileInputRef = ref<HTMLInputElement | null>(null)

  // 是否打开删除弹窗
  const isDeleteDialogOpen = ref(false)
  // 正在删除的素材id
  const deletingPictureId = ref('')
  // 正在删除的素材被屏显使用计数
  const deletingPictureUseCount = computed(() => getPictureUseCount(deletingPictureId.value))

  // 是否打开重命名弹窗
  const isRenameDialogOpen = ref(false)
  // 正在重命名的素材
  const renamingPictureId = ref('')
  // 重命名输入框内容
  const pictureName = ref('')

  const isPictureNameOverLimit = computed(
    () => Array.from(pictureName.value).length > MAX_NAME_LENGTH,
  )

  const getPictureUseCount = (id: string) => {
    if (!id) return 0

    return props.pages.filter((page) => page.id === id).length
  }

  /** 点击上传素材 */
  const handleUploadClick = () => {
    fileInputRef.value?.click()
  }

  const handleFileChange = async (event: Event) => {
    const input = event.target as HTMLInputElement
    const file = input.files?.[0]
    if (!file) return

    if (!FILE_ACCEPTS.includes(file.type as (typeof FILE_ACCEPTS)[number])) {
      const accepts = FILE_SUFFIX_ACCEPTS.join(', ')
      return toast.warning(t('toast.pages.fileAccept', { accepts }))
    }

    const item = createScreenPictureItem({
      blob: file,
      mimeType: file.type,
      pictures: props.pictures,
    })

    await savePicture(item)

    const pictures = [
      ...props.pictures,
      {
        ...item,
        url: URL.createObjectURL(item.blob),
      },
    ]
    emit('update', pictures)

    input.value = ''
  }

  const handleSelectPicture = (item: ScreenPictureItem) => {
    emit('select', item)
  }

  const handleDeletePicture = (id: string) => {
    if (props.pictures.find((picture) => picture.id === id)?.builtIn) return

    isDeleteDialogOpen.value = true
    deletingPictureId.value = id
  }

  const handleConfirmDelete = async () => {
    await removePicture(deletingPictureId.value)
    emit('update')
    isDeleteDialogOpen.value = false
    deletingPictureId.value = ''
  }

  const handleRenamePicture = (id: string) => {
    const current = props.pictures.find((picture) => picture.id === id)
    if (!current || current.builtIn) return

    renamingPictureId.value = id
    pictureName.value = current.name
    isRenameDialogOpen.value = true
  }

  const handleConfirmRename = async () => {
    const name = pictureName.value.trim()

    if (!name || isPictureNameOverLimit.value) return

    await renamePicture(renamingPictureId.value, name)

    isRenameDialogOpen.value = false
    renamingPictureId.value = ''
    pictureName.value = ''
  }

  const renamePicture = async (id: string, name: string) => {
    const pictureItem = props.pictures.find((item) => item.id === id)
    const pictureName = name.trim()
    if (
      !pictureItem ||
      pictureItem.builtIn ||
      !pictureName ||
      Array.from(name).length > MAX_NAME_LENGTH
    )
      return

    const renamedItem: UserScreenPictureItem = {
      id: pictureItem.id,
      name: pictureName,
      blob: pictureItem.blob,
      mimeType: pictureItem.mimeType,
      size: pictureItem.size,
      createdAt: pictureItem.createdAt,
    }

    await savePicture(renamedItem)

    const pictures = props.pictures.map((item) =>
      item.id === id ? { ...item, name: pictureName } : item,
    )
    emit('update', pictures)
  }
</script>

<style scoped></style>
